import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { PROJECTS } from '../../data/projects';
import styles from './SearchOverlay.module.css';

const LOGO_COLOR =
  'https://res.cloudinary.com/dhjzovpof/image/upload/v1773094633/dbae1ac767fb73178c9e75eafff0eb62f069bf78_layuwm.png';

// ── Static data for non-project searchables ─────────────────────────────────

const SERVICES_DATA = [
  {
    id: 's1',
    title: 'Road Construction & Infrastructure Development',
    image: 'https://res.cloudinary.com/dhjzovpof/image/upload/v1773523578/Frame_1000003454_erzbf7.jpg',
    keywords: ['road', 'construction', 'infrastructure', 'asphalt', 'concrete', 'earthworks', 'culvert', 'sidewalk'],
  },
  {
    id: 's2',
    title: 'Production Of Thermoplastic Road Marking Paint',
    image: 'https://res.cloudinary.com/dhjzovpof/image/upload/v1773524670/1fae49dd-6f18-4e86-a5f4-9c67c0504cc0_l95vti.jpg',
    keywords: ['thermoplastic', 'paint', 'marking', 'manufacture', 'retro', 'reflective'],
  },
  {
    id: 's3',
    title: 'Traffic Management & Road Safety Solutions',
    image: 'https://res.cloudinary.com/dhjzovpof/image/upload/v1773598248/f4aae40e-d87e-42c1-a62d-63741179aadc_lpxv7m.jpg',
    keywords: ['traffic', 'management', 'safety', 'signals', 'control', 'urban'],
  },
  {
    id: 's4',
    title: 'Road Marking & Signage Systems',
    image: 'https://res.cloudinary.com/dhjzovpof/image/upload/v1773599754/b7511241-8536-40c4-a4a2-cb5bf3b4aa26_ie7pbx.jpg',
    keywords: ['road marking', 'signage', 'signs', 'line marking', 'highway', 'municipal'],
  },
  {
    id: 's5',
    title: 'Smart Traffic & Solar Infrastructure',
    image: 'https://res.cloudinary.com/dhjzovpof/image/upload/v1773599954/ba68b07d-ee48-4dfa-9690-d5fbdacbdc1d_fmxcjh.jpg',
    keywords: ['solar', 'smart', 'renewable', 'energy', 'street lighting', 'hybrid', 'green'],
  },
];

const TEAM_DATA = [
  { id: 't1', title: 'Civil Engineers', keywords: ['civil', 'engineer', 'structural'] },
  { id: 't2', title: 'Mechanical Engineers', keywords: ['mechanical', 'engineer'] },
  { id: 't3', title: 'Electrical Engineers', keywords: ['electrical', 'engineer'] },
  { id: 't4', title: 'Project Managers', keywords: ['project', 'manager', 'management'] },
  { id: 't5', title: 'Welders', keywords: ['weld', 'welder', 'fabrication'] },
  { id: 't6', title: 'Draftsmen', keywords: ['draft', 'drafting', 'design', 'cad'] },
  { id: 't7', title: 'Plant Technicians', keywords: ['plant', 'technician', 'equipment'] },
  { id: 't8', title: 'Site Managers', keywords: ['site', 'manager', 'supervisor'] },
];

const PAGES_DATA = [
  { id: 'pg1', title: 'About Us', route: '/about', keywords: ['about', 'company', 'mission', 'vision', 'tektron'] },
  { id: 'pg2', title: 'Our Impact', route: '/impact', keywords: ['impact', 'stats', 'reach', 'states', 'projects completed'] },
  { id: 'pg3', title: 'Partners', route: '/partners', keywords: ['partners', 'dangote', 'julius berger', 'lagos state', 'collaboration'] },
  { id: 'pg4', title: 'Contact Us', route: '/contact', keywords: ['contact', 'reach', 'email', 'phone', 'enquiry'] },
  { id: 'pg5', title: 'Tektron Group', route: '/group', keywords: ['group', 'subsidiary', 'tektron group'] },
];

// ── Build flat search index ──────────────────────────────────────────────────

const SEARCH_INDEX = [
  ...PROJECTS.map(p => ({
    id: `proj-${p.id}`,
    title: p.title,
    subtitle: `${p.state} · ${p.year}`,
    type: 'Project',
    route: `/projects/${p.id}`,
    image: p.image,
    keywords: [p.title, p.category, p.state, p.location, p.type, p.client]
      .filter(Boolean)
      .map(s => s.toLowerCase()),
  })),
  ...SERVICES_DATA.map(s => ({
    id: `svc-${s.id}`,
    title: s.title,
    subtitle: 'Services',
    type: 'Service',
    route: '/services',
    image: s.image,
    keywords: s.keywords,
  })),
  ...TEAM_DATA.map(t => ({
    id: `team-${t.id}`,
    title: t.title,
    subtitle: 'Our Team',
    type: 'Team',
    route: '/team',
    image: null,
    keywords: t.keywords,
  })),
  ...PAGES_DATA.map(p => ({
    id: p.id,
    title: p.title,
    subtitle: 'Page',
    type: 'Page',
    route: p.route,
    image: null,
    keywords: p.keywords,
  })),
];

function runSearch(query) {
  const q = query.toLowerCase().trim();
  if (q.length < 2) return [];
  return SEARCH_INDEX.filter(
    item =>
      item.title.toLowerCase().includes(q) ||
      item.keywords.some(k => k.includes(q))
  ).slice(0, 24);
}

const overlayVariants = {
  hidden:  { x: '100%' },
  visible: { x: 0,      transition: { type: 'tween', duration: 0.28, ease: [0.4, 0, 0.2, 1] } },
  exit:    { x: '100%', transition: { type: 'tween', duration: 0.24, ease: [0.4, 0, 0.2, 1] } },
};

export default function SearchOverlay({ open, onClose }) {
  const [query, setQuery] = useState('');
  const inputRef = useRef(null);
  const results = runSearch(query);

  useEffect(() => {
    if (open) {
      setQuery('');
      const t = setTimeout(() => inputRef.current?.focus(), 60);
      return () => clearTimeout(t);
    }
  }, [open]);

  // Lock body scroll while open
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className={styles.overlay}
          variants={overlayVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
        >
          {/* ── Header ── */}
          <div className={styles.header}>
            <Link to="/" onClick={onClose} className={styles.logoLink}>
              <img src={LOGO_COLOR} alt="TektronPlus" className={styles.logo} />
            </Link>
            <button className={styles.closeBtn} onClick={onClose} aria-label="Close search">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M3.5 3.5L12.5 12.5M3.5 12.5L12.5 3.5" stroke="#fff" strokeWidth="1.8" strokeLinecap="round"/>
              </svg>
            </button>
          </div>

          {/* ── Search input ── */}
          <div className={styles.searchBar}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <circle cx="10.5" cy="10.5" r="6.25" stroke="#9e9e9e" strokeWidth="1.5"/>
              <path d="M15.5 15.5L20 20" stroke="#9e9e9e" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            <input
              ref={inputRef}
              type="search"
              placeholder="Search projects, services, team…"
              value={query}
              onChange={e => setQuery(e.target.value)}
              className={styles.input}
              autoComplete="off"
            />
            {query && (
              <button className={styles.clearBtn} onClick={() => setQuery('')} aria-label="Clear">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                  <path d="M2 2L12 12M2 12L12 2" stroke="#9e9e9e" strokeWidth="1.6" strokeLinecap="round"/>
                </svg>
              </button>
            )}
          </div>

          {/* ── Results ── */}
          <div className={styles.results}>
            {query.length < 2 ? (
              <div className={styles.emptyState}>
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <circle cx="10.5" cy="10.5" r="6.25" stroke="#d2d2d2" strokeWidth="1.2"/>
                  <path d="M15.5 15.5L20 20" stroke="#d2d2d2" strokeWidth="1.2" strokeLinecap="round"/>
                </svg>
                <p>Search across projects, services,<br/>team, and more.</p>
              </div>
            ) : results.length === 0 ? (
              <div className={styles.emptyState}>
                <p>No results for <strong>"{query}"</strong></p>
              </div>
            ) : (
              <>
                <p className={styles.resultsCount}>{results.length} result{results.length !== 1 ? 's' : ''}</p>
                <div className={styles.grid}>
                  {results.map(item => (
                    <Link
                      key={item.id}
                      to={item.route}
                      className={styles.card}
                      onClick={onClose}
                    >
                      <div className={styles.cardImg}>
                        {item.image ? (
                          <img src={item.image} alt="" />
                        ) : (
                          <div className={styles.cardImgPlaceholder}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                              <circle cx="10.5" cy="10.5" r="6.25" stroke="#d2d2d2" strokeWidth="1.2"/>
                              <path d="M15.5 15.5L20 20" stroke="#d2d2d2" strokeWidth="1.2" strokeLinecap="round"/>
                            </svg>
                          </div>
                        )}
                      </div>
                      <div className={styles.cardBody}>
                        <span className={`${styles.badge} ${styles['badge' + item.type]}`}>
                          {item.type}
                        </span>
                        <span className={styles.cardTitle}>{item.title}</span>
                        <span className={styles.cardSub}>{item.subtitle}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}